// frontend/app/lessonPlans.js
//
// API module for the Lesson Plan ("Lesson in a Box") domain, shaped like
// collections.js. Every call goes through the caller's `fetchWithAuth` from
// useAuth() — that is the only path with 401 refresh-and-retry, and every
// lesson-plan route requires a bearer token.

import { FASTAPI_BASE_URL } from "./config";

const API_LESSON_PLANS = `${FASTAPI_BASE_URL}/lesson-plans`;

async function handle(res) {
  let data = null;
  let text = null;
  try {
    data = await res.json();
  } catch {
    try {
      text = await res.text();
    } catch {
      text = null;
    }
  }

  if (!res.ok) {
    const errMsg =
      data?.detail?.message ||
      (typeof data?.detail === "string" ? data.detail : null) ||
      data?.message ||
      data?.error ||
      text ||
      `Request failed (${res.status}).`;
    const error = new Error(errMsg);
    error.status = res.status;
    error.detail = data?.detail ?? null;
    throw error;
  }

  return data;
}

const jsonPost = (body) => ({
  method: "POST",
  headers: { "Content-Type": "application/json" },
  body: JSON.stringify(body),
});

const query = (params) => {
  const search = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") return;
    search.append(key, String(value));
  });
  const qs = search.toString();
  return qs ? `?${qs}` : "";
};

/** List lesson plans, optionally narrowed to a board / chapter / topic. */
export async function apiListLessonPlans(
  fetchWithAuth,
  { board, chapterId, boardTopicId, sourceTopicId, includeDrafts = false } = {}
) {
  const url = `${API_LESSON_PLANS}${query({
    board,
    chapter_id: chapterId,
    board_topic_id: boardTopicId,
    source_topic_id: sourceTopicId,
    include_drafts: includeDrafts ? "true" : null,
  })}`;
  return handle(await fetchWithAuth(url));
}

/**
 * Ask the server which lessons answer a free-text description.
 *
 * Only called when local ranking (components/lessonPlans/lessonMatch.js) found
 * nothing — the words a teacher typed are matched against the list already in
 * memory first, and this is the "Saras, what did I mean?" path behind it.
 *
 * Returns `{ matches: [{ lesson_plan_id, score, reason }], source }`, where
 * `source` is "openai" or "heuristic": the server falls back to deterministic
 * ranking rather than failing, so a classroom never stalls on an API call.
 * This function still throws like every other call in this module; the finder
 * catches and shows "no matches", because a teacher who is mid-sentence has no
 * use for an error about a suggestion service.
 */
export async function apiMatchLessonPlans(
  fetchWithAuth,
  { query: text, board, limit = 5 } = {}
) {
  return handle(
    await fetchWithAuth(
      `${API_LESSON_PLANS}/match`,
      jsonPost({ query: text, board: board || null, limit })
    )
  );
}

/**
 * `{ "<board_topic_id>": count }` for a whole chapter in one call.
 * The topic tiles need this to decide whether to draw the Lesson Plan chip;
 * fetching per tile would be N round-trips on every chapter open.
 */
export async function apiLessonPlanAvailability(
  fetchWithAuth,
  { board, chapterId } = {}
) {
  const url = `${API_LESSON_PLANS}/availability${query({
    board,
    chapter_id: chapterId,
  })}`;
  return handle(await fetchWithAuth(url));
}

/** Fully resolved lesson: steps, blocks and launchable asset payloads. */
export async function apiGetLessonPlanRuntime(
  fetchWithAuth,
  lessonPlanId,
  { languageId = 1, mode = "teacher", strict = false } = {}
) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/runtime${query({
    language_id: languageId,
    mode,
    strict: strict ? "true" : null,
  })}`;
  return handle(await fetchWithAuth(url));
}

export async function apiGetLessonPlanState(
  fetchWithAuth,
  studentEmail,
  lessonPlanId
) {
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/state`;
  return handle(await fetchWithAuth(url));
}

export async function apiSaveLessonPlanState(
  fetchWithAuth,
  studentEmail,
  lessonPlanId,
  {
    stateJson = {},
    lastActiveStepKey = null,
    stepIndex = 0,
    visitedThroughIndex = 0,
    completed = false,
  } = {}
) {
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/state`;
  return handle(
    await fetchWithAuth(url, {
      ...jsonPost({
        state_json: stateJson,
        last_active_step_key: lastActiveStepKey,
        step_index: stepIndex,
        visited_through_index: visitedThroughIndex,
        completed,
      }),
      method: "PUT",
    })
  );
}

/** Write answered check blocks so Teacher Insights has real numbers. */
export async function apiRecordLessonResponses(
  fetchWithAuth,
  studentEmail,
  lessonPlanId,
  responses
) {
  if (!Array.isArray(responses) || responses.length === 0) {
    return { written: 0 };
  }
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/responses`;
  return handle(await fetchWithAuth(url, jsonPost({ responses })));
}

/**
 * Every lesson this student has started, for the lessons-page badges.
 * One call for the whole page rather than a state lookup per card.
 */
export async function apiGetLessonPlanProgress(
  fetchWithAuth,
  studentEmail,
  { board } = {}
) {
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/progress${query({ board })}`;
  return handle(await fetchWithAuth(url));
}

/**
 * The catalogue of Saras content on one topic, for the authoring asset picker.
 *
 * This is the Collections endpoint, reused rather than duplicated: it already
 * returns every video tag and PWO scene for a topic with display names. Rows
 * map straight onto lesson asset_ref triples —
 *   pwos_payload   -> { source_pwo_id, scene_name }
 *   videos_payload -> { source_video_id, tag_id }
 */
export async function apiGetTopicAssetCatalog(
  fetchWithAuth,
  { board, boardTopicId, sourceTopicId, languageId = 1 } = {}
) {
  const url = `${FASTAPI_BASE_URL}/collections/template_from_topic${query({
    board,
    board_topic_id: boardTopicId,
    source_topic_id: sourceTopicId,
    language_id: languageId,
  })}`;
  const template = await handle(await fetchWithAuth(url));

  const toInt = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  };

  return {
    // `pwo_source_id` comes back as a string; the runtime compares numerically.
    pwos: (template?.pwos_payload || [])
      .filter((row) => row?.scene_name)
      .map((row) => ({
        source_pwo_id: toInt(row.pwo_source_id ?? row.pwo_id),
        scene_name: row.scene_name,
        scene_label: row.display_name || row.scene_name,
        pwo_label: String(row.name || "").split(" - ")[0] || "",
      })),
    videos: (template?.videos_payload || []).map((row) => ({
      source_video_id: toInt(row.source_id ?? row.video_id),
      tag_id: row.tag_id ?? null,
      tag_start_time: row.tag_start_time || null,
      is_full_video: Boolean(row.is_full_video),
      tag_label: String(row.name || "").split(" - ").slice(1).join(" - ") || row.name,
      video_label: String(row.name || "").split(" - ")[0] || "",
    })),
    // The topic quiz is opened whole, never question by question, so only the
    // stem and type are carried through — enough to count them and to preview
    // what the quiz covers without holding 50-odd full question bodies in
    // component state.
    quiz: (template?.quiz_payload || []).map((row) => ({
      question_id: toInt(row.question_id),
      question: row.question || "",
      question_type: row.question_type ?? null,
    })),
  };
}

// ---------- authoring ----------
// Every mutating route requires a bearer token and teacher-like role; the
// server is the authority, these just carry the call.

/** Create a lesson. `slug` is normalized server-side; a duplicate returns 409. */
export async function apiCreateLessonPlan(fetchWithAuth, lesson) {
  return handle(await fetchWithAuth(API_LESSON_PLANS, jsonPost(lesson)));
}

/**
 * Create a DRAFT lesson from an uploaded lesson JSON document (the shape of
 * `backend/data/lesson_plans/*.json`). Resolves `{lesson, warnings}`; a 422
 * rejects with `error.detail` as a list of `{path, message}`, a 413 means the
 * document is over 1 MB.
 */
export async function apiImportLessonPlan(fetchWithAuth, document) {
  return handle(await fetchWithAuth(`${API_LESSON_PLANS}/import`, jsonPost(document)));
}

/**
 * The example lesson document for the upload button: a complete, valid lesson
 * (with a `_help` field the import ignores). Resolves to the parsed JSON.
 */
export async function apiGetLessonImportExample(fetchWithAuth) {
  return handle(await fetchWithAuth(`${API_LESSON_PLANS}/import/example`));
}

/**
 * A lesson as the document the upload accepts (teachers only). Resolves the
 * parsed JSON; the caller decides how to save it. A deleted or missing lesson
 * rejects with status 404, a student with 403.
 */
export async function apiExportLessonPlan(fetchWithAuth, lessonPlanId) {
  return handle(await fetchWithAuth(`${API_LESSON_PLANS}/${lessonPlanId}/export`));
}

/**
 * Update lesson-level fields. Passing `steps` or `concepts` REPLACES the whole
 * set and bumps `version` — for editing one step use apiSaveLessonPlanStep,
 * which is why per-step routes exist.
 */
export async function apiPatchLessonPlan(fetchWithAuth, lessonPlanId, patch) {
  return handle(
    await fetchWithAuth(`${API_LESSON_PLANS}/${lessonPlanId}`, {
      ...jsonPost(patch),
      method: "PATCH",
    })
  );
}

export async function apiDeleteLessonPlan(fetchWithAuth, lessonPlanId) {
  const res = await fetchWithAuth(`${API_LESSON_PLANS}/${lessonPlanId}`, {
    method: "DELETE",
  });
  if (!res.ok && res.status !== 204) return handle(res);
  return { deleted: true };
}

/**
 * Copy a lesson into a draft the caller owns — "Make my copy" on a lesson
 * written by someone else. No request body: everything the server needs is
 * already on the lesson being copied. Returns the same create summary shape
 * as apiCreateLessonPlan (`lesson_plan_id`, `can_edit: true`), HTTP 201.
 */
export async function apiForkLessonPlan(fetchWithAuth, lessonPlanId) {
  return handle(
    await fetchWithAuth(`${API_LESSON_PLANS}/${lessonPlanId}/fork`, {
      method: "POST",
    })
  );
}

/** Upsert a single step without rewriting the lesson's other steps. */
export async function apiSaveLessonPlanStep(fetchWithAuth, lessonPlanId, step) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/steps/${encodeURIComponent(
    step.step_key
  )}`;
  return handle(
    await fetchWithAuth(url, { ...jsonPost(step), method: "PUT" })
  );
}

export async function apiDeleteLessonPlanStep(fetchWithAuth, lessonPlanId, stepKey) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/steps/${encodeURIComponent(stepKey)}`;
  const res = await fetchWithAuth(url, { method: "DELETE" });
  if (!res.ok && res.status !== 204) return handle(res);
  return { deleted: true };
}

/** Re-assign `sequence` 1..N. `stepKeys` must be the plan's full set. */
export async function apiReorderLessonPlanSteps(fetchWithAuth, lessonPlanId, stepKeys) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/steps/reorder`;
  return handle(
    await fetchWithAuth(url, {
      ...jsonPost({ step_keys: stepKeys }),
      method: "PUT",
    })
  );
}

/** Per-concept mastery rollup for the teacher's Insights panel. */
export async function apiGetLessonClassInsights(
  fetchWithAuth,
  lessonPlanId,
  { classId } = {}
) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/class-insights${query({
    class_id: classId,
  })}`;
  return handle(await fetchWithAuth(url));
}

/**
 * TeachSense: the Learning Loop's teacher rollup — who is where right now, what
 * the class predicted before the lesson, mastery bands per concept, ranked
 * misconceptions, reflections, exploration signals and the recommendation
 * sentences.
 *
 * A separate call from class-insights rather than a bigger one: TeachSense
 * extends that panel instead of replacing it, and it reads the mastery table
 * and the exploration event stream, which the existing rollup does not touch.
 * A teacher who never opens the tab should not pay for the join.
 */
export async function apiGetLessonTeachSense(
  fetchWithAuth,
  lessonPlanId,
  { classId } = {}
) {
  const url = `${API_LESSON_PLANS}/${lessonPlanId}/teachsense${query({
    class_id: classId,
  })}`;
  return handle(await fetchWithAuth(url));
}

/* ── Saras Learning Loop ─────────────────────────────────────────────────── */

/**
 * Ask Saras to read one free-text answer.
 *
 * The prompt, rubric and concept all come from the lesson server-side — this
 * call carries only which block was answered and what the student wrote, so a
 * client cannot talk the evaluator into grading something the lesson never
 * asked. Returns the feedback plus the mastery deltas the answer produced.
 *
 * `ai_feedback.source` is "openai" or "heuristic". Render the difference: a
 * heuristic score is an automatic check, and calling it Saras AI would claim a
 * judgement nothing made.
 */
export async function apiEvaluateLessonAnswer(
  fetchWithAuth,
  studentEmail,
  lessonPlanId,
  { stepKey, blockId, text, persist = true }
) {
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/evaluate`;
  return handle(
    await fetchWithAuth(
      url,
      jsonPost({ step_key: stepKey, block_id: blockId, text, persist })
    )
  );
}

/**
 * Exploration as Assessment: how the student moved through the lesson.
 *
 * Fire-and-forget. The route drops unknown event types and counts them rather
 * than refusing the batch, and it never returns an error a lesson should
 * react to — losing a scene-switch record must not interrupt a class. These
 * signals inform TeachSense; they never move a mastery score.
 */
export async function apiRecordExplorationEvents(
  fetchWithAuth,
  studentEmail,
  lessonPlanId,
  events
) {
  if (!Array.isArray(events) || events.length === 0) {
    return { accepted: 0, rejected: 0 };
  }
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/exploration-events`;
  return handle(await fetchWithAuth(url, jsonPost({ events })));
}

/**
 * This student's bands for the lesson's declared concepts, in the lesson's own
 * order. Feeds the Grow phase's mastery_map block.
 *
 * A concept nobody has been checked on comes back `mastery: null`, not 0 —
 * unknown and none-of-it are different answers, and only one of them is true.
 * Pass `scope: "journey"` with a `journeyId` for the cross-loop rollup.
 */
export async function apiGetLessonMastery(
  fetchWithAuth,
  studentEmail,
  lessonPlanId,
  { scope, journeyId } = {}
) {
  const url = `${FASTAPI_BASE_URL}/student/${encodeURIComponent(
    studentEmail
  )}/lesson-plans/${lessonPlanId}/mastery${query({
    scope,
    journey_id: journeyId,
  })}`;
  return handle(await fetchWithAuth(url));
}
